import { z } from 'zod';
import { UploadedFile } from 'store/files';
import { AdminRole, AdminStatus } from 'store/admins';
import {
  apiIntegrationLogsFiltersSchema,
  integrationLogItemsFiltersSchema,
  integrationLogsFiltersSchema,
} from './integrations.schemas';

export const integrationStatuses = ['active', 'inactive'] as const;
export type IntegrationStatus = (typeof integrationStatuses)[number];

export const integrationModelStatuses = ['active', 'inactive'] as const;
export type IntegrationModelStatus = (typeof integrationModelStatuses)[number];

export const integrationVariants = [
  'melp_api',
  'vikarina',
  'navision',
  'bamboo-hr',
  'agroup',
  'hibob',
  'hcm',
  'webhook',
] as const;
export type IntegrationVariant = (typeof integrationVariants)[number];

export const integrationLogItemLevels = ['info', 'warning', 'error'] as const;
export type IntegrationLogItemLevel = (typeof integrationLogItemLevels)[number];

export const integrationLogMethods = [
  'GET',
  'POST',
  'PATCH',
  'PUT',
  'DELETE',
] as const;
export type IntegrationLogMethod = (typeof integrationLogMethods)[number];

export const integrationLogStatuses = [
  'running',
  'completed',
  'failed',
] as const;
export type IntegrationLogStatus = (typeof integrationLogStatuses)[number];

export interface IntegrationModel {
  id: string;
  name: string;
  status: IntegrationModelStatus;
  variant: IntegrationVariant;
  comment: string;
  logo: UploadedFile | null;
  createdAt: string;
}

export interface Integration {
  id: string;
  name: string;
  parentCompanyId: string;
  status: IntegrationStatus;
  model: IntegrationModel;
  admin: {
    id: string;
    firstName: string;
    lastName: string;
    role: AdminRole;
    status: AdminStatus;
  };
}

export interface IntegrationLog {
  clientIntegrationTypeId: string;
  processId: string;
  startDate: string;
  endDate: string;
  status: IntegrationLogStatus;
  hasWarningLogItems: boolean;
  hasErrorLogItems: boolean;
}

export interface IntegrationLogItem {
  id: string;
  processId: string;
  level: IntegrationLogItemLevel;
  creationDate: string;
  msg: {
    code: string;
    originalMsg: string;
    payload: Record<string, any>;
  };
}

export type IntegrationLogItemsFilters = z.infer<
  typeof integrationLogItemsFiltersSchema
>;

export type IntegrationLogsFilters = z.infer<
  typeof integrationLogsFiltersSchema
>;

export interface ApiIntegrationLogEvent {
  createAt: string;
  timestamp: number;
  level: IntegrationLogItemLevel;
  method: IntegrationLogMethod;
  url: string;
  request: string;
  raw: Record<string, any>;
}

export type ApiIntegrationLogsFilters = z.infer<
  typeof apiIntegrationLogsFiltersSchema
>;
